import React, { useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import api from "@/services/api";
import { KeyRound, Plus, Copy, Trash2, Loader2, Plug, CheckCircle2, BookOpen } from "lucide-react";

const BASE_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminIntegraciones() {
  const [keys, setKeys] = useState(null);
  const [showCrear, setShowCrear] = useState(false);
  const [nombre, setNombre] = useState("");
  const [creando, setCreando] = useState(false);
  const [nuevaKey, setNuevaKey] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await api.get("/integracion/admin/keys");
      setKeys(res.data);
    } catch (e) {
      toast.error("Error cargando llaves API");
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const crear = async () => {
    setCreando(true);
    try {
      const res = await api.post("/integracion/admin/keys", { nombre });
      setNuevaKey(res.data);
      setShowCrear(false);
      setNombre("");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al crear la llave");
    } finally {
      setCreando(false);
    }
  };

  const revocar = async (k) => {
    if (!window.confirm(`¿Revocar la llave "${k.nombre}"? Los sistemas que la usen perderán acceso.`)) return;
    try {
      await api.delete(`/integracion/admin/keys/${k.id}`);
      toast.success("Llave revocada");
      load();
    } catch (e) {
      toast.error("Error al revocar");
    }
  };

  const copiar = (texto, msg) => {
    navigator.clipboard.writeText(texto);
    toast.success(msg || "Copiado al portapapeles");
  };

  const descargarDoc = async () => {
    try {
      const res = await api.get("/integracion/admin/documentacion");
      const blob = new Blob([res.data.contenido], { type: "text/markdown" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "API_INTEGRACION_ODOO_BarujShefa.md";
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Documentación descargada — entrégala al consultor Odoo");
    } catch (e) {
      toast.error("Error descargando la documentación");
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-integraciones-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-[#4A2C0A] flex items-center gap-2">
            <Plug className="w-6 h-6" /> Integraciones / API
          </h1>
          <p className="text-sm text-gray-600 mt-1">
            Conecta Odoo ERP u otros sistemas externos a Baruj Shefa mediante llaves API
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={descargarDoc} data-testid="descargar-doc-btn">
            <BookOpen className="w-4 h-4 mr-2" /> Documentación Técnica
          </Button>
          <Button onClick={() => setShowCrear(true)} className="bg-[#4A2C0A]" data-testid="crear-api-key-btn">
            <Plus className="w-4 h-4 mr-2" /> Nueva Llave API
          </Button>
        </div>
      </div>

      {/* Llaves */}
      <div className="bg-white rounded-2xl border overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs text-gray-500 uppercase border-b">
              <th className="p-3">Nombre</th>
              <th className="p-3">Llave</th>
              <th className="p-3">Estado</th>
              <th className="p-3">Peticiones</th>
              <th className="p-3">Último uso</th>
              <th className="p-3"></th>
            </tr>
          </thead>
          <tbody>
            {(keys || []).map((k) => (
              <tr key={k.id} className="border-b last:border-0">
                <td className="p-3 font-medium text-[#4A2C0A]">{k.nombre}</td>
                <td className="p-3 font-mono text-xs text-gray-500">{k.key_preview}</td>
                <td className="p-3">
                  <Badge className={`border-0 ${k.activa ? "bg-green-100 text-green-800" : "bg-red-100 text-red-700"}`}>
                    {k.activa ? "Activa" : "Revocada"}
                  </Badge>
                </td>
                <td className="p-3">{k.total_requests || 0}</td>
                <td className="p-3 text-xs text-gray-500">
                  {k.ultimo_uso ? new Date(k.ultimo_uso).toLocaleString("es-CO") : "Nunca"}
                </td>
                <td className="p-3 text-right">
                  {k.activa && (
                    <Button size="sm" variant="ghost" className="text-red-500" onClick={() => revocar(k)} data-testid={`revocar-key-${k.nombre}`}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </td>
              </tr>
            ))}
            {keys?.length === 0 && (
              <tr><td colSpan="6" className="p-6 text-center text-gray-500">No hay llaves API. Crea una para conectar Odoo.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Guía de conexión */}
      <div className="bg-white rounded-2xl border p-6">
        <h2 className="font-semibold text-[#4A2C0A] flex items-center gap-2 mb-3">
          <BookOpen className="w-5 h-5" /> Guía de conexión (Odoo u otro sistema)
        </h2>
        <div className="space-y-3 text-sm text-gray-700">
          <div>
            <p className="font-medium text-[#4A2C0A]">1. URL base de la API:</p>
            <div className="flex items-center gap-2 mt-1">
              <code className="bg-gray-100 rounded px-2 py-1 text-xs font-mono flex-1 overflow-x-auto">{BASE_URL}/api/integracion/v1</code>
              <Button size="sm" variant="ghost" onClick={() => copiar(`${BASE_URL}/api/integracion/v1`)}>
                <Copy className="w-4 h-4" />
              </Button>
            </div>
          </div>
          <div>
            <p className="font-medium text-[#4A2C0A]">2. Autenticación: header <code className="bg-gray-100 px-1 rounded">X-API-Key</code> con tu llave</p>
          </div>
          <div>
            <p className="font-medium text-[#4A2C0A]">3. Endpoints disponibles:</p>
            <ul className="mt-1 space-y-1 font-mono text-xs bg-gray-50 rounded-lg p-3">
              <li>GET /ping — verificar conexión</li>
              <li>GET /productos — catálogo con referencia SKU (BS-0001) para mapear con Odoo</li>
              <li>GET /pedidos?actualizado_desde=...&estado=&requiere_factura=&page=&limit= — sync incremental</li>
              <li>GET /pedidos/{"{numero_o_id}"} — detalle con historial y datos de facturación DIAN</li>
              <li>GET /clientes — clientes únicos consolidados (res.partner)</li>
              <li>GET /ventas-carrito?desde=&hasta= — ventas físicas de vendedores</li>
              <li>GET /pedidos-mayoristas?estado= — pedidos a bodega PM</li>
              <li>GET /facturas-mayoristas?estado=pendiente — facturas FM y pagos</li>
              <li>GET /resumen-contable?fecha=YYYY-MM-DD — cierre diario por canal y método de pago</li>
            </ul>
          </div>
          <div>
            <p className="font-medium text-[#4A2C0A]">4. Ejemplo (curl):</p>
            <code className="block bg-gray-900 text-green-400 rounded-lg p-3 text-xs font-mono mt-1 overflow-x-auto">
              curl -H "X-API-Key: bs_tu_llave_aqui" {BASE_URL}/api/integracion/v1/pedidos?requiere_factura=true
            </code>
          </div>
          <p className="text-xs text-gray-500">
            💡 En Odoo, esta información se usa en un conector de "API externa / REST" o en un módulo de importación programada. Los pedidos con <code>requiere_factura: true</code> incluyen todos los datos DIAN del cliente para emitir la factura electrónica.
          </p>
        </div>
      </div>

      {/* Crear llave */}
      <Dialog open={showCrear} onOpenChange={setShowCrear}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Nueva Llave API</DialogTitle>
            <DialogDescription>Dale un nombre que identifique el sistema que la usará</DialogDescription>
          </DialogHeader>
          <Input
            placeholder="Ej: Odoo ERP"
            value={nombre}
            onChange={(e) => setNombre(e.target.value)}
            data-testid="api-key-nombre-input"
          />
          <Button onClick={crear} disabled={creando || !nombre.trim()} className="bg-[#4A2C0A] w-full" data-testid="api-key-crear-submit">
            {creando ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <KeyRound className="w-4 h-4 mr-2" />}
            Generar Llave
          </Button>
        </DialogContent>
      </Dialog>

      {/* Mostrar llave nueva (una sola vez) */}
      <Dialog open={!!nuevaKey} onOpenChange={(o) => !o && setNuevaKey(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-green-700">
              <CheckCircle2 className="w-5 h-5" /> Llave creada: {nuevaKey?.nombre}
            </DialogTitle>
            <DialogDescription>
              ⚠️ Copia esta llave AHORA. Por seguridad no se volverá a mostrar completa.
            </DialogDescription>
          </DialogHeader>
          <div className="bg-gray-900 rounded-lg p-3">
            <code className="text-green-400 text-xs font-mono break-all" data-testid="nueva-api-key-valor">{nuevaKey?.key}</code>
          </div>
          <Button onClick={() => copiar(nuevaKey?.key, "Llave copiada — pégala en Odoo")} className="bg-[#4A2C0A] w-full" data-testid="copiar-api-key-btn">
            <Copy className="w-4 h-4 mr-2" /> Copiar Llave
          </Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
